// MyContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import {
  detectUserAgent,
  getLoginInfo,
  mobileScreenSize,
} from "../utils/commonFunctions";

// 새로운 Context 생성
export const MyContext = createContext();

// Context의 Provider를 제공할 컴포넌트 생성
export const MyProvider = ({ children }) => {
  const [pageName, setPageName] = useState(""); // pagename
  const [isUpdateList, setIsUpdateList] = useState(false); // navigation 대화 목록 업데이트
  const [list, setList] = useState([]); // navigation 대화 목록
  const [storedList, setStoredList] = useState([]); //  navigation 보관함 모곩
  const [history, setHistory] = useState([]); // 대화 이력
  const [isUpdate, setIsUpdate] = useState(false); // scroll 따라가는 이벤트
  const [user, setUser] = useState({
    // 로그인 사용자 프로필 정보
    id: null,
    tone: null,
    picture_extension: null,
    picture_data: null,
    name: null,
    paid: false,
    start_dt: null,
    end_dt: null,
    tutor_cnt: 0,
    socra_cnt: 0,
  });
  const [searchContent, setSearchContent] = useState(""); // 검색어
  const pieceDataSumRef = useRef(""); // 통신 종료전 텍스트 저장
  const [pieceDataSum, setPieceDataSum] = useState(""); // 통신 종료전 텍스트 저장
  const [introText, setIntroText] = useState([]); // 대기 텍스트
  const introTextRef = useRef([]); // 대기 텍스트 ref,
  const introListRef = useRef([]);
  const [isClickedHistory, setIsClickedHistory] = useState(false);
  const [paramId, setParamId] = useState(null); // 대화 id
  const [fetching, setFetching] = useState(false); // AI 통신 상태
  const [isMobile, setIsMobile] = useState(false); // 모바일 기기 감지
  const [isLogin, setIsLogin] = useState(false); // 로그인 여부 확인
  const [referrerUrl, setReferrerUrl] = useState(""); // 로그인 후 리다이렉트할 URL
  const [type, setType] = useState(null);
  const [openNav, setOpenNav] = useState(!isMobile ? true : false); // 네비게이션 메뉴 토글
  const [isStartInterview, setIsStartInterview] = useState(false); // 면접 시작 여부(버튼 클릭, 재시작)
  const [isAddingMode, setIsAddingMode] = useState(false); // 소크라테스 나만의 폼 생성 모드
  const [isFreeScroll, setIsFreeScroll] = useState(true); // 사용자 스크롤 자유모드
  const [alertModal, setAlertModal] = useState(false); // 알림창
  const [alertSetting, setAlertSetting] = useState({
    // 알림창 컴포넌트에 전달할 props
    btnTxt: "",
    alertTxt: "",
    double: false,
    onClose: () => {
      setAlertModal(false);
      clearState();
    },
    onConfirm: () => {
      setAlertModal(false);
      clearState();
    },
  });
  const [isOpenTicketAlert, setIsOpenTicketAlert] = useState(false); // 티켓 알림창
  const [isProfile, setIsProfile] = useState(false); // 마이페이지 여부
  const [isLogoHidden, setIsLogoHidden] = useState(false); // headerscroll
  const controllerRef = useRef(new AbortController());
  const headerRef = useRef(null); // 면접 진행중 이탈 여부 체크를 위한 ref
  const [isSticky, setIsSticky] = useState(false); // 고정 상태를 관리하는 상태 변수
  const [isAppMode, setIsAppMode] = useState(false); // 앱 모드 여부
  const [appVersion, setAppVersion] = useState(null); // 앱 버전
  const [isUnderVersion, setIsUnderVersion] = useState(false); // 1.0.8 미만 버전 여부
  const navRef = useRef(null);

  // 버전 비교 함수 (예: "1.0.7" < "1.0.8" -> true)
  const compareVersion = (version, targetVersion = "1.0.8") => {
    if (!version) return true; // null이면 true
    const versionParts = version.split(".").map(Number);
    const targetParts = targetVersion.split(".").map(Number);

    for (
      let i = 0;
      i < Math.max(versionParts.length, targetParts.length);
      i++
    ) {
      const v = versionParts[i] || 0;
      const t = targetParts[i] || 0;
      if (v < t) return true;
      if (v > t) return false;
    }
    return false; // 같으면 false
  };

  const clearState = () => {
    setAlertSetting({
      ...alertSetting,
      btnTxt: "",
      alertTxt: "",
      double: false,
      onClose: () => {
        setAlertModal(false);
        clearState();
      },
      onConfirm: () => {
        setAlertModal(false);
      },
    });
  };

  const handleResize = () => {
    const viewportWidth = window.innerWidth;
    if (viewportWidth < 769) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  // 윈도우 리사이증시 사이즈 감지
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const checkAgent = detectUserAgent();
    const viewportWidth = window.innerWidth;
    setIsMobile(checkAgent === "mobile" || viewportWidth < 769);
    setOpenNav(!(checkAgent === "mobile" || viewportWidth < 769));
  }, []);

  useEffect(() => {
    const isAppMode =
      document.cookie
        .split("; ")
        .find((row) => row.startsWith("appMode="))
        ?.split("=")[1] === "Y";

    setIsAppMode(isAppMode);
  }, []);

  // 앱 버전 조회
  useEffect(() => {
    const getVersion = async () => {
      try {
        const version = await window?.Android?.getAppVersion?.();
        if (version) {
          setAppVersion(version);
          // 버전 비교
          setIsUnderVersion(compareVersion(version));
        } else {
          setIsUnderVersion(true); // 버전 조회 실패 시 true
        }
      } catch (error) {
        console.log("앱 버전 조회 실패 또는 웹 환경");
        setIsUnderVersion(true); // 오류 시 true
      }
    };
    getVersion();
  }, []);

  // 알림창 컴포넌트에 전달되는 text값에 따라 알림 모달창 여닫기
  useEffect(() => {
    if (alertSetting.alertTxt !== "") {
      setAlertModal(true);
    } else {
      setAlertModal(false);
    }
  }, [alertSetting.alertTxt]);

  useEffect(() => {
    if (!alertModal) {
      clearState();
    }
  }, [alertModal]);

  return (
    <MyContext.Provider
      value={{
        history,
        setHistory,
        pageName,
        setPageName,
        list,
        setList,
        storedList,
        setStoredList,
        isUpdate,
        setIsUpdate,
        user,
        setUser,
        paramId,
        setParamId,
        fetching,
        setFetching,
        isMobile,
        setIsMobile,
        isLogin,
        setIsLogin,
        referrerUrl,
        setReferrerUrl,
        type,
        setType,
        openNav,
        setOpenNav,
        isStartInterview,
        setIsStartInterview,
        isAddingMode,
        setIsAddingMode,
        isFreeScroll,
        setIsFreeScroll,
        alertModal,
        setAlertModal,
        alertSetting,
        setAlertSetting,
        isProfile,
        setIsProfile,
        controllerRef,
        headerRef,
        clearState,
        isLogoHidden,
        setIsLogoHidden,
        isSticky,
        setIsSticky,
        navRef,
        introText,
        setIntroText,
        introTextRef,
        pieceDataSumRef,
        pieceDataSum,
        setPieceDataSum,
        introListRef,
        searchContent,
        setSearchContent,
        isClickedHistory,
        setIsClickedHistory,
        isUpdateList,
        setIsUpdateList,
        isAppMode,
        appVersion,
        setAppVersion,
        isUnderVersion,
        setIsUnderVersion,
        isOpenTicketAlert,
        setIsOpenTicketAlert,
      }}
    >
      {children}
    </MyContext.Provider>
  );
};

export const useCommon = () => useContext(MyContext);
